import { Dna, LoaderCircle } from "lucide-react";
import { useEffect, useState, type FormEvent } from "react";
import { api } from "./api";
import "./login.css";

interface LoginScreenProps {
  checking?: boolean;
  initialError?: string;
  onAuthenticated: () => void;
}

export function LoginScreen({
  checking = false,
  initialError,
  onAuthenticated,
}: LoginScreenProps) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | undefined>(initialError);

  useEffect(() => {
    document.body.classList.add("cadence-auth-page");
    return () => document.body.classList.remove("cadence-auth-page");
  }, []);

  useEffect(() => {
    setError(initialError);
  }, [initialError]);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!username.trim() || !password) {
      setError("请输入用户名和密码。");
      return;
    }

    setSubmitting(true);
    setError(undefined);
    try {
      const session = await api.login({
        username: username.trim(),
        password,
      });
      if (!session.authenticated) {
        setError("用户名或密码不正确。");
        return;
      }
      onAuthenticated();
    } catch (cause) {
      setError(
        cause instanceof Error && cause.message.trim()
          ? cause.message
          : "登录失败，请检查凭据后重试。",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const displayedError = error;

  return (
    <main className="cadence-auth" data-testid="auth-gate">
      <section
        className="cadence-auth-panel"
        aria-labelledby="cadence-auth-title"
      >
        <header className="cadence-auth-brand" aria-label="Cadence Bio">
          <span className="cadence-auth-brand-mark" aria-hidden="true">
            <Dna size={21} />
          </span>
          <span className="cadence-auth-brand-copy">
            <span>
              <strong>Cadence</strong> <em>Bio</em>
            </span>
            <small>生物医药研究工作区</small>
          </span>
        </header>

        <div className="cadence-auth-access">
          {checking ? (
            <div
              className="cadence-auth-checking"
              aria-busy="true"
              aria-live="polite"
            >
              <LoaderCircle
                className="cadence-auth-spinner"
                size={20}
                aria-hidden="true"
              />
              <div>
                <h1 id="cadence-auth-title">正在验证会话</h1>
                <p>正在确认研究工作台访问权限。</p>
              </div>
            </div>
          ) : (
            <>
              <div className="cadence-auth-heading">
                <h1 id="cadence-auth-title">登录研究工作台</h1>
                <p>输入已分配的用户名和密码。</p>
              </div>

              <form
                className="cadence-auth-form"
                aria-busy={submitting}
                noValidate
                onSubmit={submit}
              >
                <label htmlFor="cadence-username">
                  用户名
                  <input
                    id="cadence-username"
                    name="username"
                    type="text"
                    autoFocus
                    autoComplete="username"
                    autoCapitalize="none"
                    spellCheck={false}
                    value={username}
                    disabled={submitting}
                    aria-describedby={
                      displayedError ? "cadence-auth-error" : undefined
                    }
                    onChange={(event) => {
                      setUsername(event.target.value);
                      setError(undefined);
                    }}
                  />
                </label>
                <label htmlFor="cadence-password">
                  密码
                  <input
                    id="cadence-password"
                    name="password"
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    disabled={submitting}
                    aria-describedby={
                      displayedError ? "cadence-auth-error" : undefined
                    }
                    onChange={(event) => {
                      setPassword(event.target.value);
                      setError(undefined);
                    }}
                  />
                </label>

                {displayedError && (
                  <p
                    className="cadence-auth-error"
                    id="cadence-auth-error"
                    role="alert"
                  >
                    {displayedError}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={submitting || !username.trim() || !password}
                >
                  {submitting && (
                    <LoaderCircle
                      className="cadence-auth-spinner"
                      size={16}
                      aria-hidden="true"
                    />
                  )}
                  {submitting ? "验证中…" : "登录"}
                </button>
              </form>
            </>
          )}
        </div>
      </section>
    </main>
  );
}
